import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shadcn-ui/accordion';
import { cn } from '@/shadcn-ui/utils';
import { FilterCounts } from '@/catalog/types/catalog-market-loader';
import { IndexedFilters } from '@/catalog/types/filters';
import { snakeCaseToSentenceCase } from '@/util-conversion/misc/snake-case-to-sentence-case';
import { useFilterMenuEntries } from '@/catalog/hooks/use-filter-menu-entries';
import FilterMenuCategory from './filter-menu-category';
import getPlanNameFromMiles from '@/account/utils/get-plan-name-from-miles';
import Spacer from '@/common/components/spacer';
import WaitlistLink from '../../waitlist-link';

// This list determines the order of the filters in the accordion.
const FILTER_ORDER = [
  'featured',
  'monthly_price',
  'shipping',
  'body_type',
  'makes',
  'model_year',
  'mileage',
  'fuel_type',
  'miles_per_gallon',
  'exterior_color',
  'seats',
  'drivetrain',
  'features',
  'interior_color',
];

interface FilterAccordionProps {
  filterOptions: IndexedFilters;
  selectedFilters: IndexedFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<IndexedFilters>>;
  handleClose?: () => void;
  filterCounts?: FilterCounts;
  selectedPlanMileage?: number;
  canViewSwap?: boolean;
  vehicleWaitlistEnabled?: boolean;
  waitlistLinkPrefix?: string;
  defaultAccordionValue?: string[];
}

function filterOptionDisplayTitle(categoryName: string) {
  switch (categoryName) {
    case 'makes':
      return 'Make & Model';
    case 'seats':
      return 'Number of seats';
    case 'miles_per_gallon':
      return 'Miles per gallon (MPG)';
    default:
      return snakeCaseToSentenceCase(categoryName);
  }
}

export default function FilterAccordion({
  filterOptions,
  selectedFilters,
  setSelectedFilters,
  handleClose,
  filterCounts,
  selectedPlanMileage = 850,
  canViewSwap = false,
  vehicleWaitlistEnabled = false,
  waitlistLinkPrefix,
  defaultAccordionValue = ['filter-option-featured'],
}: FilterAccordionProps) {
  const filterEntries = useFilterMenuEntries({
    filters: filterOptions,
    removeCategoriesWithoutValues: true,
    filterSortOrder: FILTER_ORDER,
  });

  return (
    <Accordion
      type="multiple"
      className="overflow-y-auto rounded-b-[16px] bg-flexcar-neutral100"
      defaultValue={defaultAccordionValue}
    >
      {filterEntries.map(([categoryName, categoryFilters], index) => {
        // Find related count based on category name
        const filterOptionCount =
          filterCounts &&
          Object.entries(filterCounts).some(([key]) => key === categoryName)
            ? filterCounts[categoryName as keyof FilterCounts]
            : undefined;

        return (
          <AccordionItem
            key={`filter-option-${categoryName}`}
            value={`filter-option-${categoryName}`}
            className={cn({
              'border-b-0': index === filterEntries.length - 1,
            })}
          >
            <AccordionTrigger
              className={cn('w-full p-[24px] hover:no-underline', {
                'rounded-b-[16px]': index === filterEntries.length - 1,
              })}
              iconSize="small"
              iconVariant="dark"
            >
              <div className="flex w-full items-center justify-between">
                <p className="body2-strong text-flexcar-neutral1">
                  {filterOptionDisplayTitle(categoryName)}
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="body px-[24px] pb-[24px]" fadeWhileClosing>
              <FilterMenuCategory
                categoryName={categoryName}
                categoryFilters={categoryFilters}
                selectedCategoryFilters={selectedFilters[categoryName]}
                categoryFilterCount={filterOptionCount}
                setSelectedFilters={setSelectedFilters}
                selectedPlanName={getPlanNameFromMiles(selectedPlanMileage)}
                canViewFooterNote={!canViewSwap || categoryName !== 'monthly_price'}
              />

              {vehicleWaitlistEnabled && categoryName === 'makes' ? (
                <>
                  <Spacer variant="height24" />
                  <WaitlistLink
                    className="body2 underline"
                    onClick={handleClose}
                    linkPrefix={waitlistLinkPrefix}
                  >
                    Still looking for the right car?
                  </WaitlistLink>
                </>
              ) : null}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
